import React, { useMemo, useEffect, useCallback, useState } from 'react';
import { Checkbox, Select, Typography, InfoRow } from '../../../../../../../components';
import { MemoryCriteria } from '../../../DiscoveryInventoryTable/components/StorageAndMemoryCriteria/MemoryCriteria';
import {
    getGiBfromMiBOneDecimal,
    getMiBFromGiB,
    parseCpuData,
    parseRamData,
    setSessionVar,
    getSessionVar,
} from '../../../../../../../lib/helpers';
import { OPTIMIZATION_TYPES, REQUIRED_OPTIONS, MAX_SOCKETS_CORES, MAX_MEMORY_GB } from '../../constants';
import { getCriteriaIcon } from '../../../DiscoveryInventoryTable/components/StorageAndMemoryCriteria/CriteriaIcons';
import { useLazyGetInstanceTypesQuery } from '../../../../../../../api/discovery/inventory';

const { Title, Text, Paragraph } = Typography;

const INSTANCE_TYPES_STORAGE_KEY = 'aws_instance_types';

export const ComputeSettings = ({
    record,
    ignoredOptimizations,
    setIgnoredOptimizations,
    additionalOptimizations,
    setAdditionalOptimizations,
    isLoading,
    scrollToBottom,
}) => {
    const cpuData = parseCpuData(record?.cpus);
    const vCpus = cpuData?.cores && cpuData?.sockets ? cpuData.cores * cpuData.sockets : null;
    const effectiveVCpus = record?.optimizations?.cpu || vCpus;
    const vCpuUnit = effectiveVCpus === 1 ? 'vCPU' : 'vCPUs';
    const vCpuLabel = effectiveVCpus != null ? `${effectiveVCpus} ${vCpuUnit}` : 'N/A';
    const ramData = parseRamData(record?.memory);
    const cpuIgnored = ignoredOptimizations?.cpu;
    const ramIgnored = ignoredOptimizations?.ram;

    const [fetchInstanceTypes] = useLazyGetInstanceTypesQuery();
    const [instanceTypes, setInstanceTypes] = useState(null);
    const [ignoreInstanceType, setIgnoreInstanceType] = useState(!record?.optimizations?.aws?.instance_type);

    // Load instance types from session storage or fetch if not cached
    useEffect(() => {
        const loadInstanceTypes = async () => {
            const cachedData = getSessionVar(INSTANCE_TYPES_STORAGE_KEY);

            if (cachedData) {
                try {
                    setInstanceTypes(cachedData);
                } catch (error) {
                    fetchAndCacheInstanceTypes();
                }
            } else {
                fetchAndCacheInstanceTypes();
            }
        };

        const fetchAndCacheInstanceTypes = async () => {
            try {
                const result = await fetchInstanceTypes({ cloudType: 'aws' }).unwrap();
                if (result?.instance_types) {
                    setInstanceTypes(result.instance_types);
                    setSessionVar(INSTANCE_TYPES_STORAGE_KEY, result.instance_types);
                }
            } catch (error) {}
        };

        loadInstanceTypes();
    }, [fetchInstanceTypes]);

    useEffect(() => {
        setIgnoreInstanceType(!record?.optimizations?.aws?.instance_type);
    }, [record]);

    const instanceTypeOptions = useMemo(() => {
        if (!instanceTypes) return [];
        return instanceTypes.map((instance) => ({
            value: instance.name,
            label: `${instance.name} (${instance.cpu_cores} CPU, ${instance.memory_gb} GiB Ram)`,
            cpu_cores: instance.cpu_cores,
            memory_gb: instance.memory_gb,
        }));
    }, [instanceTypes]);

    const buildNumberOptions = useCallback((numberOfOptions) => {
        return Array.from({ length: numberOfOptions }, (_, i) => ({ value: i + 1, label: String(i + 1) }));
    }, []);

    const socketsCoresOptions = useMemo(() => {
        return buildNumberOptions(MAX_SOCKETS_CORES);
    }, []);

    const memoryOptions = useMemo(() => {
        return buildNumberOptions(MAX_MEMORY_GB);
    }, []);

    const isIgnoreCPUOptimizationsChecked = useMemo(
        () => cpuIgnored.includes(OPTIMIZATION_TYPES.UNDERSIZED) && cpuIgnored.includes(OPTIMIZATION_TYPES.OVERSIZED),
        [ignoredOptimizations],
    );

    const isIgnoreMemoryOptimizationsChecked = useMemo(
        () => ramIgnored.includes(OPTIMIZATION_TYPES.UNDERSIZED) && ramIgnored.includes(OPTIMIZATION_TYPES.OVERSIZED),
        [ignoredOptimizations],
    );

    const isIgnoreInstanceTypeChecked = useMemo(() => ignoreInstanceType, [ignoreInstanceType]);

    const handleIgnoreOptimizations = (e, optimizationType) => {
        const isChecked = e.target.checked;

        if (optimizationType === 'cpu' || optimizationType === 'ram') {
            setIgnoredOptimizations((prev) => ({
                ...prev,
                [optimizationType]: isChecked
                    ? [
                          ...new Set([
                              ...prev[optimizationType],
                              OPTIMIZATION_TYPES.UNDERSIZED,
                              OPTIMIZATION_TYPES.OVERSIZED,
                          ]),
                      ]
                    : prev[optimizationType].filter((opt) => !REQUIRED_OPTIONS.includes(opt)),
            }));
        } else if (optimizationType === 'instanceType') {
            setIgnoreInstanceType(isChecked);
            if (isChecked) {
                setAdditionalOptimizations((prev) => ({
                    ...prev,
                    optimizations: {
                        ...prev.optimizations,
                        aws: {
                            ...prev.optimizations?.aws,
                            instance_type: '',
                        },
                    },
                }));
            }
        }

        scrollToBottom();
    };

    const handleSetSockets = (value) => {
        const isDefault = value === cpuData.sockets;

        setAdditionalOptimizations((prev) => {
            const coresValue = prev.optimizations.cores || '';
            const effectiveCores = coresValue || cpuData.cores;

            return {
                ...prev,
                optimizations: {
                    ...prev.optimizations,
                    sockets: isDefault ? '' : value,
                    // Only keep a cpu override when sockets OR cores are overridden
                    cpu: isDefault && !coresValue ? '' : effectiveCores * value,
                },
            };
        });

        scrollToBottom();
    };

    const handleSetCoresPerSocket = (value) => {
        const isDefault = value === cpuData.cores;

        setAdditionalOptimizations((prev) => {
            const socketsValue = prev.optimizations.sockets || '';
            const effectiveSockets = socketsValue || cpuData.sockets;

            return {
                ...prev,
                optimizations: {
                    ...prev.optimizations,
                    cores: isDefault ? '' : value,
                    // Only keep a cpu override when sockets OR cores are overridden
                    cpu: isDefault && !socketsValue ? '' : effectiveSockets * value,
                },
            };
        });

        scrollToBottom();
    };

    const handleSetMemory = (value) => {
        const newRamMiB = getMiBFromGiB(value);
        const isDefaultRam = newRamMiB === ramData.size;
        setAdditionalOptimizations((prev) => ({
            ...prev,
            optimizations: {
                ...prev.optimizations,
                ram: isDefaultRam ? '' : newRamMiB,
            },
        }));

        scrollToBottom();
    };

    const handleSetInstanceType = (value) => {
        setAdditionalOptimizations((prev) => ({
            ...prev,
            optimizations: {
                ...prev.optimizations,
                aws: {
                    ...prev.optimizations?.aws,
                    instance_type: value,
                },
            },
        }));

        scrollToBottom();
    };

    return (
        <div className="compute-settings">
            <section className="settings-subsection">
                <Title level={5}>Current CPU & Memory</Title>

                <div className="info-rows">
                    <InfoRow
                        label="CPU"
                        value={
                            <>
                                {vCpuLabel}{' '}
                                {getCriteriaIcon(record?.cpu_utilization)}
                            </>
                        }
                    />
                    <MemoryCriteria
                        criteria={record?.ram_utilization}
                        total={record?.ram_total}
                        used={record?.ram_used}
                    />
                </div>
            </section>

            <section className="settings-subsection">
                <Checkbox
                    checked={isIgnoreCPUOptimizationsChecked}
                    onChange={(e) => handleIgnoreOptimizations(e, 'cpu')}
                    disabled={isLoading}
                >
                    Ignore CPU Optimizations
                </Checkbox>
            </section>

            <section className="settings-subsection">
                <Text className="bold">Change CPUs</Text>
                <div className="cpu-options">
                    <div className="cpu-option-row">
                        <InfoRow value="Number of sockets:" />
                        <Select
                            className="sockets-select"
                            value={additionalOptimizations?.optimizations?.sockets || cpuData.sockets}
                            onChange={handleSetSockets}
                            options={socketsCoresOptions}
                            disabled={isIgnoreCPUOptimizationsChecked}
                            size="small"
                        />
                    </div>
                    <div className="cpu-option-row">
                        <InfoRow value="Number of Cores per Socket:" />
                        <Select
                            className="cores-select"
                            value={additionalOptimizations?.optimizations?.cores || cpuData.cores}
                            onChange={handleSetCoresPerSocket}
                            options={socketsCoresOptions}
                            disabled={isIgnoreCPUOptimizationsChecked}
                            size="small"
                        />
                    </div>
                    <div className="cpu-option-row">
                        <InfoRow
                            value={`New vCPU quantity based on selection: ${additionalOptimizations?.optimizations?.cpu || record.cpu_total}`}
                        />
                    </div>
                </div>
            </section>

            <section className="settings-subsection">
                <Checkbox
                    checked={isIgnoreMemoryOptimizationsChecked}
                    onChange={(e) => handleIgnoreOptimizations(e, 'ram')}
                    disabled={isLoading}
                >
                    Ignore Memory Optimizations
                </Checkbox>
            </section>

            <section className="settings-subsection">
                <Text className="bold">Change Memory</Text>
                <div className="memory-options">
                    <InfoRow value="Ram:" />
                    <div className="memory-option-row">
                        <Select
                            value={
                                getGiBfromMiBOneDecimal(additionalOptimizations?.optimizations?.ram) ||
                                getGiBfromMiBOneDecimal(ramData.size)
                            }
                            onChange={handleSetMemory}
                            options={memoryOptions}
                            disabled={isIgnoreMemoryOptimizationsChecked}
                            size="small"
                        />
                        <span className="memory-unit">GB</span>
                    </div>
                </div>
            </section>

            <section className="settings-subsection">
                <Checkbox
                    checked={isIgnoreInstanceTypeChecked}
                    onChange={(e) => handleIgnoreOptimizations(e, 'instanceType')}
                    disabled={isLoading}
                >
                    Ignore Instance Type Recommendation
                </Checkbox>
            </section>
            <section className="settings-subsection">
                <Select
                    className="instance-type-select"
                    placeholder="Select instance type"
                    disabled={isIgnoreInstanceTypeChecked}
                    value={additionalOptimizations?.optimizations?.aws?.instance_type || undefined}
                    onChange={handleSetInstanceType}
                    options={instanceTypeOptions}
                    showSearch
                    filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
                />
                <Paragraph className="recommendations-text">
                    Recommendations based on usage or below settings.
                </Paragraph>
            </section>
        </div>
    );
};
